import {
  followUpStatusLabel,
  type HwcgNoticeStepReportDto,
  type HwcgNoticeStepRecipientRow,
} from './hwcgNoticeStepReportModel';

export type HwcgContactNoticeStatus = {
  notificationId: string | null;
  sentAt: string | null;
  deliveryState: string | null;
  step1Response: string | null;
  step2Response: string | null;
  step2StatusLabel: string;
  responseNote: string | null;
};

const EMPTY_STATUS: HwcgContactNoticeStatus = {
  notificationId: null,
  sentAt: null,
  deliveryState: null,
  step1Response: null,
  step2Response: null,
  step2StatusLabel: '—',
  responseNote: null,
};

export function deliveryStateLabel(state: string | null | undefined): string {
  if (!state) return '—';
  if (state === 'acknowledged') return 'Acknowledged';
  if (state === 'delivered') return 'Delivered';
  if (state === 'sent') return 'Sent';
  if (state === 'failed') return 'Failed';
  if (state === 'unreachable') return 'Unreachable';
  return state;
}

/**
 * Join contact list rows to one or more hwcg-steps reports via userGuid and the
 * per-contact notification id map.
 */
export function mergeHwcgContactNoticeStatus(params: {
  contacts: { id: string; userGuid?: string | null }[];
  contactNotificationIds: Record<string, string>;
  reportsByNotificationId: Record<string, HwcgNoticeStepReportDto | undefined>;
}): Record<string, HwcgContactNoticeStatus> {
  const byUser = new Map<string, Map<string, HwcgNoticeStepRecipientRow>>();
  for (const [notificationId, report] of Object.entries(
    params.reportsByNotificationId
  )) {
    if (!report) continue;
    const users = new Map<string, HwcgNoticeStepRecipientRow>();
    for (const row of report.recipients) {
      users.set(row.userGuid, row);
    }
    byUser.set(notificationId, users);
  }

  const out: Record<string, HwcgContactNoticeStatus> = {};
  for (const contact of params.contacts) {
    const notificationId = params.contactNotificationIds[contact.id] ?? null;
    if (!notificationId) {
      out[contact.id] = { ...EMPTY_STATUS, notificationId };
      continue;
    }
    const report = params.reportsByNotificationId[notificationId];
    const row = contact.userGuid
      ? byUser.get(notificationId)?.get(contact.userGuid)
      : undefined;
    if (row?.sentAt && row.deliveryState) {
      out[contact.id] = {
        notificationId,
        sentAt: row.sentAt,
        deliveryState: row.deliveryState,
        step1Response: row.step1Response,
        step2Response: row.step2Response,
        step2StatusLabel: followUpStatusLabel(row.step2Status),
        responseNote: row.responseNote ?? null,
      };
      continue;
    }
    out[contact.id] = {
      notificationId,
      sentAt: row?.sentAt ?? report?.sentAt ?? null,
      deliveryState: row?.deliveryState ?? 'sent',
      step1Response: row?.step1Response ?? null,
      step2Response: row?.step2Response ?? null,
      step2StatusLabel: row ? followUpStatusLabel(row.step2Status) : '—',
      responseNote: row?.responseNote ?? null,
    };
  }
  return out;
}

export type WorkspaceEocOutcome = {
  outcome: 'activated' | 'cannot';
  note: string | null;
};

/** The incident-level EOC choice returned by group actions. */
export function parseWorkspaceEocOutcome(
  value: unknown
): WorkspaceEocOutcome | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as { outcome?: unknown; note?: unknown };
  if (record.outcome !== 'activated' && record.outcome !== 'cannot')
    return null;
  const note = typeof record.note === 'string' ? record.note.trim() : '';
  return {
    outcome: record.outcome,
    note: record.outcome === 'cannot' && note ? note : null,
  };
}

export function workspaceEocOutcomeSummary(
  outcome: WorkspaceEocOutcome | null
): { label: string; note: string | null } {
  if (outcome?.outcome === 'activated') {
    return { label: 'EOC activated', note: null };
  }
  if (outcome?.outcome === 'cannot') {
    return { label: 'EOC cannot be activated', note: outcome.note };
  }
  return { label: 'EOC outcome has not been recorded.', note: null };
}

export function eocTabOutcome(
  status: HwcgContactNoticeStatus | undefined
): 'activated' | 'cannot' | null {
  const raw = (status?.step2Response || status?.step1Response || '').trim();
  if (raw === 'EOC activated' || raw === 'Activated') return 'activated';
  if (raw === 'EOC cannot be activated') return 'cannot';
  return null;
}

const EOC_ACTIVATED_LABELS = new Set([
  'EOC activated',
  'Activated',
  'Reported',
]);

/**
 * One column for an EOC notice: the parent reply, or the follow-up reply when
 * an older two-step send has one.
 */
export function hwcgEocNoticeResponse(
  status: HwcgContactNoticeStatus | undefined
): string {
  if (!status?.notificationId) return '—';
  const raw = (status.step2Response || status.step1Response || '').trim();
  if (!raw) return 'Awaiting';
  if (EOC_ACTIVATED_LABELS.has(raw)) return 'Activated';
  return raw;
}

export function formatNoticeSentAt(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}
